(function () {
  'use strict';

  const ENGINE_VERSION = '6.3.0-professional-profile';
  const ROLES = ['oil_cleanser', 'cleanser', 'toner', 'essence', 'treatment', 'moisturizer', 'sunscreen'];
  const STRONG_ACIDS = new Set(['aha', 'bha', 'pha', 'benzoyl_peroxide']);
  const RETINOIDS = new Set(['retinoid', 'retinol', 'retinal', 'tretinoin']);
  const INTENSITY_RANK = { gentle: 1, moderate: 2, strong: 3, specialist: 4 };
  const EXPERIENCE_RANK = { beginner: 1, intermediate: 2, advanced: 3 };
  const ATTR_CACHE = new WeakMap();
  const PROFESSIONAL_CACHE = new WeakMap();
  const PRODUCT_TEXT_CACHE = new WeakMap();
  const PACKAGE_SIZE_CACHE = new WeakMap();
  const BASE_NAME_CACHE = new WeakMap();
  const ROLE_INDEX_CACHE = new WeakMap();

  const PREFERRED_BRANDS = new Set([
    'centellian24', 'purcell', 'aestura', 'axisy', 'celimax', 'cellfusionc',
    'fancl', 'dhc', 'genabelle', 'heveblue', 'isoi', 'ongredients',
    'rovectin', 'vt', 'snature'
  ]);

  function uniq(values) {
    return Array.from(new Set((values || []).filter(Boolean)));
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, Number(value || 0)));
  }

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  function price(product) {
    const amount = product && product.variant && product.variant.price
      ? Number(product.variant.price.amount || 0)
      : 0;
    return Number.isFinite(amount) ? amount : 0;
  }


  function packageSize(product) {
    if (product && typeof product === 'object' && PACKAGE_SIZE_CACHE.has(product)) return PACKAGE_SIZE_CACHE.get(product);
    const title = String(product && product.title || '').toLowerCase().replace(',', '.');
    const multi = title.match(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(ml|g)\b/);
    let value = null;
    if (multi) value = Number(multi[1]) * Number(multi[2]);
    else {
      const matches = Array.from(title.matchAll(/(\d+(?:\.\d+)?)\s*(ml|g)\b/g));
      if (matches.length) {
        const parsed = Number(matches[matches.length - 1][1]);
        value = Number.isFinite(parsed) ? parsed : null;
      }
    }
    if (product && typeof product === 'object') PACKAGE_SIZE_CACHE.set(product, value);
    return value;
  }


  function baseProductName(product) {
    if (product && typeof product === 'object' && BASE_NAME_CACHE.has(product)) return BASE_NAME_CACHE.get(product);
    const value = normalizeText(product && product.title)
      .replace(/\b\d+\s*[x×]\s*\d+(?:[.,]\d+)?\s*(?:ml|g)\b/g, ' ')
      .replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|g)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (product && typeof product === 'object') BASE_NAME_CACHE.set(product, value);
    return value;
  }

  function hasLargerEquivalent(product, context) {
    const size = packageSize(product);
    if (size === null) return false;
    const base = baseProductName(product);
    if (!base) return false;
    const sizeMap = context && context.roleIndex && context.roleIndex.__maxTreatmentSizeByBase;
    if (!sizeMap) return false;
    return Number(sizeMap[base] || 0) >= size + 5;
  }

  function vendorCompact(product) {
    return normalizeText(product && product.vendor).replace(/[^a-z0-9]+/g, '');
  }

  function isPreferredVendor(product) {
    // Premium preference is curated. Brand-name prefixes (e.g. "Dr.") are
    // not evidence of professional quality.
    return PREFERRED_BRANDS.has(vendorCompact(product));
  }

  function strategyModule() {
    if (window.SkinQuizStrategy) return window.SkinQuizStrategy;
    throw new Error('SkinQuizStrategy is required before SkinQuizEngine.');
  }

  function normalizeProfile(input) {
    if (input && typeof input === 'object' && input.__skinQuizEngineNormalized === ENGINE_VERSION) return input;
    const normalized = strategyModule().normalizeProfile(input || {});
    if (normalized && typeof normalized === 'object') {
      Object.defineProperty(normalized, '__skinQuizEngineNormalized', { value: ENGINE_VERSION, enumerable: false });
    }
    return normalized;
  }

  function productText(product) {
    if (product && typeof product === 'object' && PRODUCT_TEXT_CACHE.has(product)) return PRODUCT_TEXT_CACHE.get(product);
    const value = normalizeText([
      product && product.title,
      product && product.productType,
      product && product.handle,
      product && product.otherTags,
      ...((product && product.productTypesRaw) || [])
    ].filter(Boolean).join(' '));
    if (product && typeof product === 'object') PRODUCT_TEXT_CACHE.set(product, value);
    return value;
  }

  function inferFormatFallback(product) {
    const t = productText(product);
    if (/\bset\b|\bkit\b|bundle|routine set|ritual set/.test(t)) return 'set';
    if (/(serum.*cream|cream.*serum|serum.*creme|creme.*serum)/.test(t)) return 'hybrid_serum_cream';
    if (/(spray serum|serum spray|mist serum|serum mist)/.test(t)) return 'spray_serum';
    if (/\bampoule\b|ampulle|ampule|ampulla/.test(t)) return 'ampoule';
    if (/\bserum\b/.test(t)) return 'serum';
    if (/essence|essenz/.test(t)) return 'essence';
    if (/sun stick|sunstick|\bstick\b/.test(t)) return 'stick';
    if (/cushion|pact/.test(t)) return 'cushion';
    if (/cream|creme|moisturizer/.test(t)) return 'cream';
    if (/toner|mist/.test(t)) return 'toner';
    return product && product.role ? product.role : 'unknown';
  }

  function concernStrategies(concerns) {
    const out = [];
    (concerns || []).forEach(c => {
      if (c === 'blemishes') out.push('blemish_control');
      if (c === 'pores') out.push('pore_refinement');
      if (c === 'pigmentation') out.push('pigment_correction');
      if (c === 'redness' || c === 'sensitivity') out.push('redness_support', 'barrier_support');
      if (c === 'dryness') out.push('hydration', 'barrier_support');
      if (c === 'dullness') out.push('glow');
      if (c === 'anti_age' || c === 'lines' || c === 'firmness') out.push('anti_age');
    });
    return uniq(out);
  }

  function fallbackProfessional(product, a) {
    const format = inferFormatFallback(product);
    const isBundle = format === 'set' || product.role === 'bundle';
    const strategies = concernStrategies(a.concerns || []);
    const active = new Set(a.actives || []);
    const add = value => { if (!strategies.includes(value)) strategies.push(value); };
    if (active.has('bha') || active.has('niacinamide')) add('pore_refinement');
    if (active.has('bha') || active.has('niacinamide') || active.has('sulfur')) add('sebum_control');
    if (active.has('azelaic') || active.has('bha') || active.has('benzoyl_peroxide')) add('blemish_control');
    if (active.has('tranexamic') || active.has('arbutin') || active.has('vitamin_c') || active.has('azelaic') || active.has('kojic')) add('pigment_correction');
    if (active.has('vitamin_c') || active.has('arbutin') || active.has('niacinamide')) add('glow');
    if (active.has('retinoid') || active.has('bakuchiol')) add('anti_age');
    if (active.has('aha') || active.has('bha') || active.has('pha') || active.has('retinoid') || active.has('niacinamide')) add('texture_refinement');

    const risk = Math.max(Number(product && product.irritationRisk || 0), Number(product && product.advancedTreatmentRisk || 0));
    const intensity = risk >= 3.5 ? 'specialist' : risk >= 2.5 ? 'strong' : risk >= 1.2 ? 'moderate' : 'gentle';

    return {
      isBundle,
      format,
      strategies,
      therapeuticStrategies: strategies.slice(),
      supportStrategies: [],
      intensity,
      anchorEligible: !isBundle && product.role === 'treatment' && ['serum', 'ampoule'].includes(format) && strategies.length > 0,
      therapeuticEssenceEligible: !isBundle && product.role === 'essence' && strategies.length > 0 && (a.actives || []).length > 0,
      primarySpf: product.role === 'sunscreen' && !['stick', 'cushion', 'powder'].includes(format) && !/\bmist\b|\bspray\b|gesichtsspray|face spray|facial spray|\bbb cream\b|\bcc cream\b|foundation/.test(productText(product)),
      spfLevel: (() => { const m = productText(product).match(/(?:spf|lsf)\s*([0-9]{2,3})/); return m ? Number(m[1]) : null; })(),
      reapplicationSpf: product.role === 'sunscreen' && (['stick', 'cushion', 'powder'].includes(format) || /\bmist\b|\bspray\b|gesichtsspray|face spray|facial spray/.test(productText(product)))
    };
  }

  function attrs(product) {
    if (product && typeof product === 'object' && ATTR_CACHE.has(product)) return ATTR_CACHE.get(product);
    const q = product && product.quiz ? product.quiz : {};
    const value = {
      skinTypes: uniq(q.skinTypes || product.skinTypes || []),
      concerns: uniq(q.concerns || product.concerns || []),
      steps: uniq(q.steps || [product.role]),
      dayparts: uniq(q.dayparts || []),
      actives: uniq(q.actives || product.activeFamilies || []),
      strength: q.strength || 'beginner',
      sensitive: typeof q.sensitive === 'boolean' ? q.sensitive : null,
      retinoidCompatible: typeof q.retinoidCompatible === 'boolean' ? q.retinoidCompatible : true,
      acidCompatible: typeof q.acidCompatible === 'boolean' ? q.acidCompatible : true,
      priorityScore: clamp(q.priorityScore || 0, 0, 10),
      irritationLoad: Number.isFinite(Number(q.irritationLoad)) ? clamp(q.irritationLoad, 0, 5) : null,
      moisturizerWeight: q.moisturizerWeight || '',
      spfUsage: q.spfUsage || '',
      cleanserIntensity: q.cleanserIntensity || ''
    };
    if (product && typeof product === 'object') ATTR_CACHE.set(product, value);
    return value;
  }

  function professional(product) {
    if (product && typeof product === 'object' && PROFESSIONAL_CACHE.has(product)) return PROFESSIONAL_CACHE.get(product);
    const a = attrs(product);
    const p = product && product.professional ? product.professional : fallbackProfessional(product || {}, a);
    const value = {
      isBundle: Boolean(p.isBundle),
      format: p.format || inferFormatFallback(product),
      strategies: uniq(p.strategies || []),
      therapeuticStrategies: uniq(p.therapeuticStrategies || p.strategies || []),
      supportStrategies: uniq(p.supportStrategies || []),
      strategyEvidence: Object.assign({}, p.strategyEvidence || {}),
      supportEvidence: Object.assign({}, p.supportEvidence || {}),
      declaredActives: uniq(p.declaredActives || []),
      detectedActives: uniq(p.detectedActives || p.declaredActives || []),
      activeConcentrations: Object.assign({}, p.activeConcentrations || {}),
      inciOnlyActives: uniq(p.inciOnlyActives || []),
      intensity: p.intensity || 'gentle',
      anchorEligible: Boolean(p.anchorEligible),
      therapeuticEssenceEligible: Boolean(p.therapeuticEssenceEligible),
      primarySpf: typeof p.primarySpf === 'boolean' ? p.primarySpf : product.role === 'sunscreen',
      spfLevel: Number.isFinite(Number(p.spfLevel)) ? Number(p.spfLevel) : null,
      reapplicationSpf: Boolean(p.reapplicationSpf),
      qualityTier: p.qualityTier || 'standard',
      priority: Number(p.priority || 0),
      sensitiveSuitability: p.sensitiveSuitability || 'conditional',
      barrierCompatibility: p.barrierCompatibility || 'medium',
      irritationLoad: Number.isFinite(Number(p.irritationLoad)) ? Number(p.irritationLoad) : (a.irritationLoad === null ? null : Number(a.irritationLoad)),
      cautions: uniq(p.cautions || []),
      primaryTarget: String(p.primaryTarget || p.clinicalProfile?.primary_target || '').toLowerCase(),
      roleSpecific: Object.assign({}, p.roleSpecific || {}),
      daypartPolicy: Object.assign({}, p.daypartPolicy || {}),
      source: p.source || 'inferred',
      profileVersion: p.profileVersion || null
    };
    if (product && typeof product === 'object') PROFESSIONAL_CACHE.set(product, value);
    return value;
  }

  function hasDeclaredActive(product, name) {
    return professional(product).declaredActives.includes(name);
  }

  function hasRetinoid(a) {
    return (a.actives || []).some(x => RETINOIDS.has(x));
  }

  function hasAcid(a) {
    return (a.actives || []).some(x => STRONG_ACIDS.has(x));
  }

  function isSpicule(product) {
    return Number(product && product.advancedTreatmentRisk || 0) > 0 ||
      (attrs(product).actives || []).some(x => /spicule|reedle/.test(x));
  }

  function available(product) {
    if (!product || !product.eligibility || !product.eligibility.eligible) return false;
    if (!product.availableForSale || !product.variant || !product.variant.availableForSale) return false;
    const quantity = product.variant.quantityAvailable;
    if (quantity !== null && quantity !== undefined && quantity !== '') {
      const q = Number(quantity);
      if (Number.isFinite(q) && q <= 0) return false;
    }
    return true;
  }

  function isStandardFaceScope(product) {
    const text = productText(product);
    if (!text) return true;
    const explicitlyFacial = /\bface\b|\bfacial\b|gesicht|skin care|skincare|sun cream|sunscreen/.test(text);
    const nonFaceExclusive = [
      /\blip\b/, /lippen/, /\beye\b/, /augen/, /\bbody\b/, /korper/, /\bhand\b/, /hande/,
      /\bfoot\b/, /fuss/, /\bhair\b/, /haare/, /scalp/, /kopfhaut/
    ].some(pattern => pattern.test(text));
    return !nonFaceExclusive || explicitlyFacial;
  }

  function moisturizerWeight(product) {
    const a = attrs(product);
    if (a.moisturizerWeight) return a.moisturizerWeight;
    const t = productText(product);
    if (/\bbalm\b|balsam|barrier cream|intensive cream|rich cream|nourishing cream|ceramide cream|repair cream/.test(t)) return 'rich';
    if (/aloe.*gel|soothing gel|water gel|\bgel\b|lotion|emulsion|fluid moisturizer/.test(t)) return 'light';
    return 'balanced';
  }

  function spfUsage(product) {
    const a = attrs(product);
    if (a.spfUsage) return a.spfUsage;
    const p = professional(product);
    if (p.reapplicationSpf) return 'reapplication';
    return 'primary';
  }

  function cleanserIntensity(product) {
    const a = attrs(product);
    if (a.cleanserIntensity) return a.cleanserIntensity;
    const t = productText(product);
    if (/deep pore|deep cleansing|pore cleansing|soda|scrub|peeling|exfoliat|enzyme powder|cleansing powder|white clay|\bclay\b|kaolin|charcoal/.test(t)) return 'deep';
    if (/gentle|mild|amino acid|low ph|barrier|sensitive/.test(t)) return 'gentle';
    return 'standard';
  }

  function productIrritation(product) {
    const a = attrs(product);
    if (a.irritationLoad !== null) return a.irritationLoad;
    let load = Math.max(Number(product && product.irritationRisk || 0), Number(product && product.advancedTreatmentRisk || 0));
    if (hasRetinoid(a)) load = Math.max(load, 3);
    if (hasAcid(a)) load = Math.max(load, 2.5);
    if (isSpicule(product)) load = Math.max(load, 3.6);
    if (product.role === 'cleanser' && cleanserIntensity(product) === 'deep') load += 1;
    return Math.round(clamp(load, 0, 5) * 10) / 10;
  }

  function routineIrritationLimit(inputProfile) {
    const profile = normalizeProfile(inputProfile);
    if (profile.barrierCompromised) return 1.8;
    if (profile.sensitivity >= 4) return 2.2;
    if (profile.sensitivity >= 3) return 2.8;
    if (profile.activeExperience === 'advanced') return 4.6;
    if (profile.activeExperience === 'intermediate') return 3.8;
    return 3;
  }

  function scheduleFor(product, profile) {
    const p = professional(product);
    const policy = p.daypartPolicy || {};
    if (product.role === 'sunscreen') return 'AM';
    if (product.role === 'oil_cleanser') return 'PM';
    if (policy.am_allowed === true && policy.pm_allowed === true) return 'AM/PM';
    if (policy.pm_allowed === true && policy.am_allowed === false) return 'PM';
    if (policy.am_allowed === true && policy.pm_allowed === false) return 'AM';
    const a = attrs(product);
    // Fallback only. Explicit v6.2 product intelligence is concentration-aware,
    // so the mere presence of BHA/AHA/PHA no longer forces PM here.
    if (hasDeclaredActive(product, 'retinoid')) return 'PM';
    return !a.dayparts.length ? 'AM/PM' : (a.dayparts.includes('AM') && a.dayparts.includes('PM') ? 'AM/PM' : a.dayparts.includes('PM') ? 'PM' : 'AM');
  }

  function frequencyFor(product) {
    return String((professional(product).daypartPolicy || {}).frequency || 'once_daily');
  }

  function routineIrritationLoads(products) {
    let am = 0;
    let pm = 0;
    (products || []).forEach(product => {
      const load = productIrritation(product);
      const schedule = scheduleFor(product);
      if (schedule === 'AM' || schedule === 'AM/PM') am += load;
      if (schedule === 'PM' || schedule === 'AM/PM') pm += load;
    });
    return { am: Math.round(am * 10) / 10, pm: Math.round(pm * 10) / 10, max: Math.round(Math.max(am, pm) * 10) / 10 };
  }

  function supportsDaypart(product, part) {
    const a = attrs(product);
    return !a.dayparts.length || a.dayparts.includes(part);
  }

  function supportsWholeDayCore(product, role) {
    if (!['cleanser', 'moisturizer'].includes(role)) return true;
    return supportsDaypart(product, 'AM') && supportsDaypart(product, 'PM');
  }

  function stableHash(value) {
    const text = String(value || '');
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function profileSignature(profile, role, salt) {
    // Only clinically relevant answers may influence equivalence tie-breaking.
    // Routine style and budget shape architecture/feasibility elsewhere; they
    // must not rotate the treatment or foundation product by themselves.
    const parts = [
      profile.skinType || '', profile.primaryConcern || '', profile.primaryChoice || '',
      (profile.secondaryConcerns || []).slice().sort().join('+'), String(profile.sensitivity || 0),
      profile.barrierState || (profile.barrierCompromised ? 'compromised' : 'stable'), role || '', salt || ''
    ];
    if (['treatment', 'essence', 'toner'].includes(role)) {
      parts.push(profile.activeExperience || '', profile.excludeRetinoids ? 'no-retinoid' : '',
        profile.excludeAcids ? 'no-acid' : '', profile.excludeSpicules ? 'no-spicule' : '');
    }
    return parts.join('|');
  }

  function primaryEvidence(product, profile, context) {
    const p = professional(product);
    const e = p.strategyEvidence || {};
    const strategy = context && context.strategy;
    const primary = strategy && strategy.originalPrimaryStrategy ? strategy.originalPrimaryStrategy : ({
      blemishes: 'blemish_control', pores: 'pore_refinement', pigmentation: 'pigment_correction',
      redness: 'redness_support', anti_age: 'anti_age', lines: 'anti_age', firmness: 'anti_age',
      glass_skin: 'glass_skin', dryness: 'hydration'
    }[profile.primaryConcern] || 'hydration');
    if (primary === 'glass_skin') {
      const h = Number(e.hydration || 0), g = Number(e.glow || 0);
      return Math.min(h, g + 0.25);
    }
    return Number(e[primary] || 0);
  }

  function secondaryBenefit(product, profile) {
    const e = professional(product).strategyEvidence || {};
    const map = { dryness: 'hydration', blemishes: 'blemish_control', pores: 'pore_refinement', pigmentation: 'pigment_correction', redness: 'redness_support', dullness: 'glow', lines: 'anti_age', firmness: 'anti_age' };
    const values = (profile.secondaryConcerns || []).map(c => Number(e[map[c]] || 0)).map(v => v < 1.5 ? 0 : Math.min(1, (v - 1.5) / 1.5));
    return values.length ? values.reduce((a,b) => a+b,0) / values.length : 0;
  }

  function treatmentSafetyScore(product, profile, state) {
    const p = professional(product);
    let score = 0;
    const ss = p.sensitiveSuitability, bc = p.barrierCompatibility;
    if (ss === 'high') score += 3; else if (ss === 'low') score -= 5;
    if (bc === 'high') score += 3; else if (bc === 'low') score -= 5;
    score -= Number(p.irritationLoad || productIrritation(product) || 0) * 1.5;
    const ir = INTENSITY_RANK[p.intensity] || 1;
    const target = INTENSITY_RANK[(contextlessStateMax(profile,state))] || 2;
    if (ir > target) score -= 8 * (ir-target);
    return score;
  }

  function contextlessStateMax(profile, state) {
    if (state && (state.state === 'reactive' || state.state === 'barrier_compromised')) return 'gentle';
    return profile.activeExperience === 'advanced' ? 'strong' : 'moderate';
  }

  function hardExclude(product, profile, role, selected, context) {
    const a = attrs(product);
    const p = professional(product);
    const rs = p.roleSpecific || {};
    const reasons = [];
    const strategy = context && context.strategy;
    const state = context && context.state ? context.state : strategyModule().analyzeSkinState(profile);
    const architecture = context && context.architecture;

    if (!available(product)) reasons.push('not_available_or_eligible');
    if (!isStandardFaceScope(product)) reasons.push('not_standard_face_scope');
    if (p.isBundle) reasons.push('bundle_not_single_product');
    if (!a.steps.includes(role) && product.role !== role) reasons.push('wrong_step');
    if (!supportsWholeDayCore(product, role)) reasons.push('core_step_not_am_pm');
    if (profile.veganOnly && !product.vegan) reasons.push('not_vegan');
    if (profile.crueltyFreeOnly && !product.crueltyFree) reasons.push('not_cruelty_free');
    if (profile.fragranceFreeOnly && product.fragranceStatus !== 'free') reasons.push('not_confirmed_fragrance_free');

    const declared = new Set(p.declaredActives || []);
    if (profile.excludeRetinoids && declared.has('retinoid')) reasons.push('retinoid_excluded');
    if (profile.excludeAcids && ['aha','bha','pha','benzoyl_peroxide'].some(x => declared.has(x))) reasons.push('acid_excluded');
    if (profile.excludeSpicules && (declared.has('spicule') || isSpicule(product))) reasons.push('spicule_excluded');
    if (strategy) {
      if ((strategy.avoidFamilies || []).includes('retinoid') && declared.has('retinoid')) reasons.push('strategy_retinoid_exclusion');
      if ((strategy.avoidFamilies || []).some(x => ['aha','bha','pha','benzoyl_peroxide'].includes(x)) && ['aha','bha','pha','benzoyl_peroxide'].some(x => declared.has(x))) reasons.push('strategy_acid_exclusion');
      if ((strategy.avoidFamilies || []).includes('spicule') && (declared.has('spicule') || isSpicule(product))) reasons.push('strategy_spicule_exclusion');
    }

    if (['treatment','essence','toner'].includes(role)) {
      const maxIntensity = strategy ? strategy.maxIntensity : contextlessStateMax(profile, state);
      if ((INTENSITY_RANK[p.intensity] || 1) > (INTENSITY_RANK[maxIntensity] || 1)) reasons.push('too_intense_for_profile');
      if (profile.sensitiveSkinType && p.sensitiveSuitability === 'low') reasons.push('stable_sensitive_low_suitability');
      if (state.state === 'reactive' && (p.intensity !== 'gentle' || p.sensitiveSuitability === 'low' || p.barrierCompatibility === 'low')) reasons.push('reactive_treatment_safety_gate');
      if (state.state === 'barrier_compromised' && (p.intensity !== 'gentle' || p.sensitiveSuitability !== 'high' || p.barrierCompatibility !== 'high' || Number(p.irritationLoad || 0) > 1.2)) reasons.push('compromised_treatment_safety_gate');
    }

    if (role === 'sunscreen' && !p.primarySpf) reasons.push('not_primary_spf');
    if (role === 'moisturizer' && ['toner','spray_serum','cushion','stick','powder','balm','set'].includes(p.format)) reasons.push('invalid_primary_moisturizer_format');
    if (role === 'moisturizer' && ['retinoid','aha','bha','pha','benzoyl_peroxide','spicule'].some(x => declared.has(x))) reasons.push('corrective_active_not_core_moisturizer');
    if (role === 'moisturizer' && /color correct|colour correct|tone.?up|bb cream|cc cream/.test(productText(product))) reasons.push('decorative_corrector_not_core_moisturizer');

    if (role === 'treatment' && context && context.anchorSelection) {
      if (!primaryAnchorIdentityEligible(product, profile, context)) reasons.push('primary_anchor_identity_mismatch');
      if (!p.anchorEligible && !primaryAnchorIdentityEligible(product, profile, context)) reasons.push('not_anchor_eligible');
      if (architecture && architecture.anchorFormats && !architecture.anchorFormats.includes(p.format)) reasons.push('anchor_format');
      const size = packageSize(product);
      if (hasLargerEquivalent(product, context)) reasons.push('mini_variant_not_primary_anchor');
      if (architecture && architecture.lowBudget && size !== null && size < 15) reasons.push('low_budget_mini_anchor');
      if (state.state === 'barrier_compromised') {
        const recovery = Math.max(Number((p.strategyEvidence||{}).barrier_support||0), Number((p.supportEvidence||{}).barrier_support||0), Number((p.strategyEvidence||{}).redness_support||0), Number((p.strategyEvidence||{}).hydration||0));
        if (recovery < 1.6) reasons.push('insufficient_recovery_evidence');
      } else if (primaryEvidence(product, profile, context) < 1.5) reasons.push('anchor_missing_primary_strategy_evidence');
    }
    if (role === 'essence' && context && context.anchorSelection) reasons.push('essence_not_primary_anchor_v53');

    if (role === 'cleanser') {
      const strip = String(rs.stripping_risk || '');
      if ((state.currentBarrierUnstable || profile.skinType === 'dry' || profile.sensitiveSkinType) && strip === 'high') reasons.push('high_stripping_for_profile');
      // Reactive/compromised skin should not spend its cleanser slot on an
      // exfoliating wash. Corrective work belongs in the treatment step; the
      // cleanser must preserve tolerance and barrier support.
      if (state.currentBarrierUnstable && rs.exfoliating === true) reasons.push('exfoliating_cleanser_for_unstable_barrier');
    }

    const selectedAttrs = (selected || []).map(attrs);
    if (selectedAttrs.some(hasRetinoid) && !a.retinoidCompatible) reasons.push('not_retinoid_compatible');
    if (selectedAttrs.some(hasAcid) && !a.acidCompatible) reasons.push('not_acid_compatible');
    return uniq(reasons);
  }

  function strategyFitScore(product, strategy, role) {
    if (!strategy || !['treatment','essence'].includes(role)) return 0;
    const p = professional(product), e = p.strategyEvidence || {};
    if (strategy.originalPrimaryStrategy === 'glass_skin') {
      const h = Number(e.hydration || 0), g = Number(e.glow || 0);
      return Math.round((Math.min(h / 2.3, 1) * 9 + Math.min(g / 2.2, 1) * 8) * 10) / 10;
    }
    if (strategy && strategy.name && strategy.name.indexOf('barrier') >= 0 && strategy.anchorStrategy === 'barrier_support') {
      const recovery = Math.max(Number(e.barrier_support||0), Number((p.supportEvidence||{}).barrier_support||0), Number(e.redness_support||0), Number(e.hydration||0));
      return recovery * 6;
    }
    const key = strategy.originalPrimaryStrategy || strategy.anchorStrategy;
    return Number(e[key] || 0) * 6;
  }

  function skinFitScore(product, profile, role) {
    const types = new Set(attrs(product).skinTypes || []);
    const foundation = ['cleanser','moisturizer','sunscreen'].includes(role);
    let score = 0;
    if (profile.skinType === 'unsure') {
      const behavioral = [...types].filter(x => ['dry','oily','combination','normal'].includes(x));
      if (types.has('all')) score += foundation ? 5 : 2;
      else if (behavioral.length >= 3) score += foundation ? 3 : 1;
      else if (behavioral.length === 1) score -= foundation ? 6 : 2;
    } else if (types.has(profile.skinType)) score += foundation ? 7 : 4;
    else if (types.has('all')) score += foundation ? 3 : 1;
    else if ([...types].some(x => ['dry','oily','combination','normal'].includes(x))) score -= foundation ? 4 : 1.5;

    if (profile.sensitiveSkinType && types.has('sensitive')) score += foundation ? 1.5 : 1;
    // Mature is a stable trait/tie-break, never a hidden anti-aging vote.
    if (profile.matureSkinType && types.has('mature')) score += role === 'moisturizer' ? 1.5 : role === 'treatment' ? 1 : 0.3;
    return score;
  }

  function levelValue(value) { return ({ low: 1, medium: 2, high: 3 }[String(value||'').toLowerCase()] || 0); }
  function suitabilityValue(value) { return ({ low: 1, conditional: 2, medium: 2, high: 3 }[String(value||'').toLowerCase()] || 0); }

  function stateToleranceScore(product, profile, state, strategy, role) {
    const p = professional(product);
    let score = 0;
    const ss = suitabilityValue(p.sensitiveSuitability), bc = levelValue(p.barrierCompatibility);
    const cautions = new Set(p.cautions || []);
    if (state.state === 'barrier_compromised') {
      score += ss * 3 + bc * 3 - Number(p.irritationLoad || 0) * 3;
    } else if (state.state === 'reactive') {
      score += ss * 2.5 + bc * 2.5 - Number(p.irritationLoad || 0) * 2.2;
    } else if (state.state === 'sometimes') {
      score += ss * 1.2 + bc * 0.8 - Number(p.irritationLoad || 0) * 0.8;
    } else if (profile.sensitiveSkinType) {
      score += ss * 1.5 + bc * 0.7 - Number(p.irritationLoad || 0) * 0.8;
    } else score -= Number(p.irritationLoad || 0) * 0.3;

    // Fragrance/aromatic cautions are not a universal ban, but they are a
    // meaningful tie-break against otherwise comparable treatments for a
    // sensitive or currently unstable profile.
    if ((profile.sensitiveSkinType || state.currentBarrierUnstable) &&
        (cautions.has('fragrance_or_aromatic_allergens') || cautions.has('essential_oils'))) score -= 1.25;

    if (['treatment','essence'].includes(role)) {
      const target = INTENSITY_RANK[strategy.intensityTarget] || 2, actual = INTENSITY_RANK[p.intensity] || 1;
      if (actual === target) score += 3;
      else if (actual < target) score += 1.5;
      else score -= 6 * (actual-target);
    }
    return score;
  }

  function treatmentBufferRequired(selected) {
    const anchor = (selected || []).find(x => x && x.role === 'treatment');
    if (!anchor) return false;
    const p = professional(anchor), d = new Set(p.declaredActives || []);
    return p.intensity === 'strong' || Number(p.irritationLoad||0) >= 2 || ['retinoid','aha','bha','pha','benzoyl_peroxide','spicule'].some(x => d.has(x));
  }

  function supportIdentityPenalty(product, profile, state, role) {
    if (!['cleanser','moisturizer','sunscreen'].includes(role)) return 0;
    const p = professional(product), rs = p.roleSpecific || {};
    const text = normalizeText([product.title, rs.archetype].filter(Boolean).join(' '));
    const selected = new Set([profile.primaryConcern, ...(profile.secondaryConcerns || [])]);
    let penalty = 0;
    const scale = role === 'moisturizer' ? 1 : 0.45;
    if (!selected.has('anti_age') && !selected.has('lines') && !selected.has('firmness') && /anti.?age|anti.?wrinkle|wrinkle|lifting|firming|retinol|retinal|youth|collagen|pdrn/.test(text)) penalty -= 5 * scale;
    if (!selected.has('pigmentation') && /dark spot|pigment|mela|brighten|alpha.?arbutin|tone/.test(text)) penalty -= 3.5 * scale;
    if (!selected.has('pores') && !selected.has('blemishes') && /pore|blackhead|acne|blemish/.test(text)) penalty -= 3 * scale;
    if (!selected.has('redness') && !profile.sensitiveSkinType && state.state === 'stable' && /calm|sooth|cica|centella|heartleaf|redness/.test(text)) penalty -= profile.primaryConcern === 'glass_skin' ? 4 * scale : 1.5 * scale;
    return penalty;
  }

  function functionalScore(product, profile, state, role, selected) {
    const p = professional(product), rs = p.roleSpecific || {};
    const identityPenalty = supportIdentityPenalty(product, profile, state, role);
    if (role === 'cleanser') {
      let score = 0;
      const strip = levelValue(rs.stripping_risk), removal = levelValue(rs.makeup_removal);
      const intensity = String(rs.cleansing_intensity || attrs(product).cleanserIntensity || 'standard');
      if (state.currentBarrierUnstable || profile.skinType === 'dry' || profile.sensitiveSkinType) score += strip ? (4-strip)*4 : 2;
      else if (profile.skinType === 'oily') score += intensity === 'standard' ? 5 : intensity === 'deep' ? 3 : 2;
      else score += intensity === 'standard' ? 4 : intensity === 'gentle' ? 4 : 1;
      if (profile.makeupFrequency === 'most_days') score += removal * 2;
      else if (profile.makeupFrequency === 'sometimes') score += removal;
      return score + identityPenalty;
    }
    if (role === 'oil_cleanser') {
      const removal = levelValue(rs.makeup_removal);
      return profile.makeupFrequency === 'most_days' ? 8 + removal*2 : profile.makeupFrequency === 'sometimes' ? 4 + removal : -1;
    }
    if (role === 'moisturizer') {
      let score = 0;
      const weight = String(rs.weight || attrs(product).moisturizerWeight || 'medium');
      const desired = profile.skinType === 'oily' ? 'light' : (profile.skinType === 'dry' ? 'rich' : 'medium');
      if (weight === desired) score += 8;
      else if (desired === 'rich' && weight === 'medium') score += 5;
      else if (desired === 'light' && weight === 'medium') score += 4;
      else if (profile.skinType === 'unsure' && weight === 'medium') score += 6;
      else score -= 3;
      if (state.hydrationNeed === 'elevated') score += levelValue(rs.hydration_strength) * 2;
      if (state.currentBarrierUnstable) score += levelValue(rs.barrier_lipid_strength) * 3 + levelValue(rs.soothing_strength) * 2;
      if (profile.sensitiveSkinType) score += levelValue(rs.soothing_strength);
      if (treatmentBufferRequired(selected)) score += levelValue(rs.treatment_buffering) * 2.5;
      return score + identityPenalty;
    }
    if (role === 'sunscreen') {
      if (!p.primarySpf) return -40;
      let score = p.spfLevel >= 50 ? 12 : p.spfLevel >= 40 ? 9 : p.spfLevel >= 30 ? 5 : p.spfLevel ? -3 : 7;
      const texture = String(rs.texture||''), hyd = levelValue(rs.hydration), makeup = levelValue(rs.makeup_compatibility);
      if (profile.skinType === 'oily') score += ['gel','essence','fluid','milk'].includes(texture) ? 4 : -1;
      if (profile.skinType === 'dry') score += hyd * 1.5;
      if (profile.skinType === 'combination') score += ['gel','essence','fluid','milk'].includes(texture) ? 2 : 0;
      if (profile.makeupFrequency === 'most_days') score += makeup*1.5;
      if (profile.primaryConcern === 'pigmentation' && rs.pigmentation_support === 'enhanced') score += 2;
      // Tone-up is a finish preference the quiz does not ask for; it is never a
      // therapeutic consequence of pigmentation.
      if (rs.tone_up === true) score -= 2.5;
      if (rs.white_cast === 'possible') score -= 0.6;
      if (state.currentBarrierUnstable || profile.sensitiveSkinType) score += levelValue(p.sensitiveSuitability) + levelValue(p.barrierCompatibility);
      return score + identityPenalty;
    }
    return 0;
  }

  function concernEvidenceScore(product, profile, role) {
    // Only the treatment role may use concern tags, and even there they are
    // weak corroboration. Support products are selected by functional vectors.
    if (role !== 'treatment') return 0;
    const a = attrs(product); let score = 0;
    if (profile.primaryConcern && a.concerns.includes(profile.primaryConcern)) score += 0.6;
    return score;
  }

  function compatibilityScore(product, selected) {
    const a = attrs(product); const selectedAttrs = (selected || []).map(attrs); let score = 2;
    if ((hasRetinoid(a) || hasAcid(a)) && selectedAttrs.some(x => (hasRetinoid(a)&&hasRetinoid(x)) || (hasAcid(a)&&hasAcid(x)))) score -= 7;
    return score;
  }

  function commercialQualityScore(product) {
    const p = professional(product);
    const tier = ({ value: -1.5, standard: 0, preferred: 1.5, premium: 2, luxury: 2 }[p.qualityTier] || 0);
    let score = tier + Number(p.priority || 0);
    const rating=Number(product.rating||0), count=Number(product.ratingCount||0);
    if (rating>=4.5 && count>0) score += Math.min(1.2, 0.3 + Math.log10(Math.max(1,count))*0.25);
    return Math.round(score*10)/10;
  }

  function fitTierFor(role, clinical) {
    const thresholds = role === 'treatment' || role === 'essence'
      ? [15, 28, 40, 52]
      : [8, 18, 28, 38];
    if (clinical >= thresholds[3]) return 5;
    if (clinical >= thresholds[2]) return 4;
    if (clinical >= thresholds[1]) return 3;
    if (clinical >= thresholds[0]) return 2;
    return 1;
  }

  function scoreProduct(product, inputProfile, role, selected, context) {
    const profile = normalizeProfile(inputProfile);
    const state = context && context.state ? context.state : strategyModule().analyzeSkinState(profile);
    const strategy = context && context.strategy ? context.strategy : strategyModule().generateStrategy(profile, state);
    const architecture = context && context.architecture ? context.architecture : strategyModule().buildRoutineArchitecture(profile, strategy, state);
    const ctx = Object.assign({}, context || {}, { state, strategy, architecture });
    const exclusions = hardExclude(product, profile, role, selected || [], ctx);
    if (exclusions.length) return { score: -Infinity, clinicalScore: -Infinity, fitTier: 0, exclusions, reasons: [] };

    const reasons = [];
    const add = (points, reason) => {
      const rounded = Math.round(Number(points || 0) * 10) / 10;
      if (!rounded) return;
      reasons.push({ points: rounded, reason });
    };

    const strategyFit = strategyFitScore(product, strategy, role); add(strategyFit, 'strategy_fit');
    const skin = skinFitScore(product, profile, role); add(skin, 'skin_fit');
    const stateFit = stateToleranceScore(product, profile, state, strategy, role); add(stateFit, 'state_tolerance');
    const functional = functionalScore(product, profile, state, role, selected || []); add(functional, 'functional_fit');
    const concern = concernEvidenceScore(product, profile, role); add(concern, 'concern_evidence');
    const compatibility = compatibilityScore(product, selected || []); add(compatibility, 'routine_compatibility');

    let clinical = Math.round(reasons.reduce((sum, item) => sum + item.points, 0) * 10) / 10;
    if (vendorCompact(product) === 'aplb') clinical = Math.round(clinical * 0.8 * 10) / 10;
    const commercial = commercialQualityScore(product);
    const fitTier = fitTierFor(role, clinical);

    return {
      score: clinical,
      clinicalScore: clinical,
      skinFitScore: clinical,
      commercialScore: commercial,
      finalScore: Math.round((clinical + commercial) * 10) / 10,
      fitTier,
      exclusions: [],
      reasons,
      strategy: strategy.name
    };
  }

  function productForRole(product, role) {
    return product;
  }

  function buildRoleIndex(catalog) {
    if (catalog && typeof catalog === 'object' && ROLE_INDEX_CACHE.has(catalog)) return ROLE_INDEX_CACHE.get(catalog);
    const index = Object.fromEntries(ROLES.map(role => [role, []]));
    (catalog || []).forEach(product => {
      const steps = attrs(product).steps;
      ROLES.forEach(role => {
        if (product.role === role || steps.includes(role)) index[role].push(product);
      });
    });
    const maxTreatmentSizeByBase = {};
    (index.treatment || []).forEach(product => {
      if (!available(product)) return;
      const base = baseProductName(product);
      const size = packageSize(product);
      if (!base || size === null) return;
      maxTreatmentSizeByBase[base] = Math.max(Number(maxTreatmentSizeByBase[base] || 0), size);
    });
    Object.defineProperty(index, '__maxTreatmentSizeByBase', { value: maxTreatmentSizeByBase, enumerable: false });
    if (catalog && typeof catalog === 'object') ROLE_INDEX_CACHE.set(catalog, index);
    return index;
  }

  function rankRole(catalog, inputProfile, role, selected, context) {
    const profile = normalizeProfile(inputProfile);
    const source = context && context.roleIndex && context.roleIndex[role]
      ? context.roleIndex[role]
      : (catalog || []);
    return source
      .map(product => {
        const evaluation = scoreProduct(product, profile, role, selected || [], context || {});
        return { product, evaluation };
      })
      .filter(item => Number.isFinite(item.evaluation.clinicalScore))
      .sort((a, b) =>
        b.evaluation.clinicalScore - a.evaluation.clinicalScore ||
        b.evaluation.commercialScore - a.evaluation.commercialScore ||
        price(a.product) - price(b.product) ||
        String(a.product.title || '').localeCompare(String(b.product.title || ''))
      );
  }

  function equivalentClinicalPool(ranked, gap) {
    if (!ranked || !ranked.length) return [];
    const best = ranked[0].evaluation.clinicalScore;
    return ranked.filter(item => item.evaluation.clinicalScore >= best - Number(gap || 0));
  }

  function removeClearlyDominatedPremium(items) {
    // Preserve diversity among near-equivalent premium choices. Only remove a
    // candidate when another option is materially better on both clinical fit
    // and quality, or equal clinically but substantially better in quality.
    return (items || []).filter((candidate, index, all) => !all.some((other, otherIndex) => {
      if (index === otherIndex) return false;
      const dc = Number(other.evaluation.clinicalScore || 0) - Number(candidate.evaluation.clinicalScore || 0);
      const dq = Number(other.evaluation.commercialScore || 0) - Number(candidate.evaluation.commercialScore || 0);
      return (dc >= 0.75 && dq >= 0.15) || (dc >= -0.01 && dq >= 0.9);
    }));
  }

  function deterministicChoice(items, profile, role, salt) {
    if (!items || !items.length) return null;
    if (items.length === 1) return items[0];
    const ordered = items.slice().sort((a, b) =>
      b.evaluation.clinicalScore - a.evaluation.clinicalScore ||
      b.evaluation.commercialScore - a.evaluation.commercialScore ||
      price(a.product) - price(b.product) ||
      String(a.product.id || a.product.title || '').localeCompare(String(b.product.id || b.product.title || ''))
    );
    const maxPool = role === 'sunscreen' ? 10 : role === 'cleanser' ? 8 : role === 'moisturizer' ? 6 : 4;
    const top = ordered.slice(0, Math.min(maxPool, ordered.length));
    return top[stableHash(profileSignature(profile, role, salt)) % top.length];
  }

  function primaryAnchorIdentityEligible(product, profile, context) {
    const p = professional(product);
    const rs = p.roleSpecific || {};
    const e = p.strategyEvidence || {};
    const target = String(p.primaryTarget || '').toLowerCase();
    const archetype = String(rs.archetype || '').toLowerCase();
    const concern = String(profile.primaryConcern || '').toLowerCase();
    const map = {
      blemishes: { targets: ['blemishes'], archetypes: ['blemish_control'], evidence: 'blemish_control' },
      pores: { targets: ['pores'], archetypes: ['pore_refinement'], evidence: 'pore_refinement' },
      pigmentation: { targets: ['pigmentation'], archetypes: ['pigment_correction'], evidence: 'pigment_correction' },
      redness: { targets: ['redness'], archetypes: ['redness_support'], evidence: 'redness_support' },
      anti_age: { targets: ['anti_age'], archetypes: ['anti_age'], evidence: 'anti_age' },
      lines: { targets: ['anti_age'], archetypes: ['anti_age'], evidence: 'anti_age' },
      firmness: { targets: ['anti_age'], archetypes: ['anti_age'], evidence: 'anti_age' },
      slow_aging: { targets: ['anti_age'], archetypes: ['anti_age'], evidence: 'anti_age' },
      glass_skin: { targets: ['glass_skin'], archetypes: ['hydration','glow'], evidence: 'glass_skin' },
      dryness: { targets: ['hydration','glass_skin'], archetypes: ['hydration'], evidence: 'hydration' },
      dehydration: { targets: ['hydration','glass_skin'], archetypes: ['hydration'], evidence: 'hydration' }
    }[concern];
    if (!map) return Number(primaryEvidence(product, profile, context) || 0) >= 2.0;
    const evidence = Number(e[map.evidence] || 0);
    const targetMatch = map.targets.includes(target);
    const archetypeMatch = map.archetypes.includes(archetype);
    // A treatment anchor must have a declared therapeutic identity for the
    // selected primary problem. Generic support evidence is insufficient.
    if (targetMatch && evidence >= 1.8) return true;
    if (archetypeMatch && evidence >= 2.2) return true;
    return false;
  }

  function treatmentIdentityAffinity(product, profile) {
    const p = professional(product), a = String((p.roleSpecific || {}).archetype || '');
    const primary = ({blemishes:'blemish_control',pores:'pore_refinement',pigmentation:'pigment_correction',redness:'redness_support',anti_age:'anti_age',glass_skin:'glass_skin'})[profile.primaryConcern] || '';
    if (profile.primaryConcern === 'glass_skin') {
      if (a === 'glow' || a === 'hydration') return 3;
      if (a === 'barrier_support') return 1.5;
      return 0;
    }
    if (a === primary) return 3;
    if ((primary === 'blemish_control' && a === 'pore_refinement') || (primary === 'pore_refinement' && a === 'blemish_control')) return 1.5;
    if (primary === 'redness_support' && a === 'barrier_support') return 1;
    return 0;
  }

  function treatmentSkinSpecificity(product, profile, state) {
    const types = new Set(attrs(product).skinTypes || []);
    let score = 0;
    if (profile.skinType === 'dry' && types.has('dry') && !types.has('oily')) score += 1.5;
    if (profile.skinType === 'oily' && types.has('oily') && !types.has('dry')) score += 0.75;
    if (profile.skinType === 'combination' && types.has('combination') && !(types.has('dry') && types.has('oily'))) score += 0.75;
    if (profile.skinType === 'normal' && types.has('normal') && types.size <= 2) score += 1.0;
    if (profile.sensitiveSkinType && types.has('sensitive')) score += 1.0;
    if (state && state.currentBarrierUnstable && professional(product).barrierCompatibility === 'high') score += 1.2;
    return score;
  }

  function treatmentSuitabilityScore(item, profile, context) {
    const product = item.product;
    const state = context && context.state ? context.state : strategyModule().analyzeSkinState(profile);
    const strategy = context && context.strategy ? context.strategy : strategyModule().generateStrategy(profile, state);
    return skinFitScore(product, profile, 'treatment') +
      stateToleranceScore(product, profile, state, strategy, 'treatment') +
      treatmentSkinSpecificity(product, profile, state) +
      treatmentIdentityAffinity(product, profile) +
      Number(item.evaluation.commercialScore || 0) * 0.25;
  }

  function choose(ranked, profile, role, context) {
    if (!ranked || !ranked.length) return null;
    if (role === 'treatment' && context && context.anchorSelection) {
      let pool = ranked.slice();
      const strategy = context.strategy || {};
      const state = context.state || {};
      if (profile.primaryConcern === 'glass_skin') {
        const secondaryStrategies = new Set((profile.secondaryConcerns || []).map(c => ({lines:'anti_age',firmness:'anti_age',pigmentation:'pigment_correction',pores:'pore_refinement',blemishes:'blemish_control',redness:'redness_support'}[c])).filter(Boolean));
        const glassNative = new Set(['hydration','glow','barrier_support','redness_support','supportive']);
        const neutral = pool.filter(item => {
          const p = professional(item.product), a = String((p.roleSpecific||{}).archetype || '');
          return glassNative.has(a) || secondaryStrategies.has(a) || (p.roleSpecific||{}).treatment_class === 'recovery';
        });
        if (neutral.length) pool = neutral;
      }
      if (strategy.name === 'gentle_texture_balance') {
        const gentle = pool.filter(item => {
          const p = professional(item.product);
          return p.intensity === 'gentle' && p.barrierCompatibility !== 'low';
        });
        if (gentle.length) pool = gentle;
      }
      // Reactive/compromised safety is an eligibility gate in hardExclude.
      // Now preserve the user's primary goal inside that valid safety pool.
      const evidences = pool.map(item => primaryEvidence(item.product, profile, context));
      const bestPrimary = evidences.length ? Math.max(...evidences) : 0;
      // Evidence is an eligibility gate, not the final ranking. Once a product
      // is clinically strong enough for the primary goal, skin/state fit,
      // treatment identity and secondary fit must be allowed to differentiate
      // genuinely equivalent professional options. Secondary concerns tighten
      // the window because they add a real clinical constraint.
      const slack = (profile.secondaryConcerns || []).length ? 0.45 : 0.75;
      const primaryPool = pool.filter(item => primaryEvidence(item.product, profile, context) >= Math.max(1.5, bestPrimary - slack));
      if (primaryPool.length) pool = primaryPool;
      pool.sort((a,b) =>
        secondaryBenefit(b.product, profile) - secondaryBenefit(a.product, profile) ||
        treatmentSuitabilityScore(b, profile, context) - treatmentSuitabilityScore(a, profile, context) ||
        primaryEvidence(b.product, profile, context) - primaryEvidence(a.product, profile, context) ||
        b.evaluation.finalScore - a.evaluation.finalScore ||
        price(a.product) - price(b.product) ||
        String(a.product.title||'').localeCompare(String(b.product.title||''))
      );
      return pool[0] || ranked[0];
    }
    // No artificial/random rotation: different products win only when the
    // answers and functional fit actually make them better.
    return ranked[0];
  }

  function isPrimaryTreatmentFormat(product, role) {
    const p = professional(product);
    if (role === 'treatment') return ['serum', 'ampoule'].includes(p.format);
    if (role === 'essence') return p.therapeuticEssenceEligible;
    return false;
  }

  function isActiveAnchorCandidate(product, inputProfile, role, context) {
    const profile = normalizeProfile(inputProfile);
    const p = professional(product);
    const architecture = context && context.architecture ? context.architecture : strategyModule().buildRoutineArchitecture(profile);
    if (p.isBundle) return false;
    if (role === 'treatment') return ['serum', 'ampoule'].includes(p.format) && primaryAnchorIdentityEligible(product, inputProfile, context);
    if (role === 'essence') return !architecture.lowBudget && p.therapeuticEssenceEligible;
    return false;
  }

  function collapseClinicallyEquivalentTreatmentDuplicates(items, profile) {
    const groups = new Map();
    (items || []).forEach(item => {
      const p = professional(item.product);
      const archetype = String((p.roleSpecific || {}).archetype || '').trim().toLowerCase();
      const primary = Math.round(primaryEvidence(item.product, profile, {}) * 10) / 10;
      const skin = Math.round(Number(item.evaluation.skinFitScore || 0) * 10) / 10;
      const state = Math.round(Number(item.evaluation.clinicalScore || 0) * 10) / 10;
      const vendor = vendorCompact(item.product);
      // Only collapse true equivalents: same vendor + treatment archetype +
      // essentially identical primary/skin evidence. Different therapeutic
      // identities, brands or clinically meaningful fit remain independent.
      const key = [vendor, archetype, primary, skin, state].join('|');
      const current = groups.get(key);
      if (!current ||
          secondaryBenefit(item.product, profile) > secondaryBenefit(current.product, profile) ||
          (secondaryBenefit(item.product, profile) === secondaryBenefit(current.product, profile) && item.evaluation.commercialScore > current.evaluation.commercialScore)) {
        groups.set(key, item);
      }
    });
    return Array.from(groups.values());
  }

  function anchorRankings(catalog, profile, selected, context) {
    const anchorContext = Object.assign({}, context, { anchorSelection: true });
    const ranked = rankRole(catalog, profile, 'treatment', selected || [], anchorContext)
      .filter(item => isActiveAnchorCandidate(item.product, profile, 'treatment', context))
      .map(item => Object.assign({}, item, { anchorRole: 'treatment' }));
    return collapseClinicallyEquivalentTreatmentDuplicates(ranked, profile);
  }

  function roleResultFromChoice(chosen, alternatives) {
    return {
      product: chosen.product,
      score: chosen.evaluation.score,
      clinicalScore: chosen.evaluation.clinicalScore,
      skinFitScore: chosen.evaluation.skinFitScore,
      commercialScore: chosen.evaluation.commercialScore,
      finalScore: chosen.evaluation.finalScore,
      fitTier: chosen.evaluation.fitTier,
      reasons: chosen.evaluation.reasons,
      alternatives: (alternatives || []).filter(x => x.product.id !== chosen.product.id).slice(0, 5).map(x => ({
        product: x.product,
        score: x.evaluation.score,
        clinicalScore: x.evaluation.clinicalScore,
        skinFitScore: x.evaluation.skinFitScore,
        commercialScore: x.evaluation.commercialScore,
        finalScore: x.evaluation.finalScore,
        fitTier: x.evaluation.fitTier,
        reasons: x.evaluation.reasons
      }))
    };
  }

  function minimumFeasiblePrice(ranked, gap) {
    if (!ranked || !ranked.length) return Infinity;
    // Reservation is not product selection. Every item in `ranked` has already
    // passed hard professional eligibility, so reserve the cheapest valid
    // support option rather than the cheapest near-top option. This protects
    // the therapeutic anchor under tight budgets and lets compromises happen
    // in support roles, where they are clinically less consequential.
    const prices = ranked.map(item => price(item.product)).filter(p => p > 0);
    return prices.length ? Math.min(...prices) : Infinity;
  }

  function chooseWithinBudget(ranked, profile, role, context, remainingBudget, reserve) {
    if (!ranked || !ranked.length) return null;
    const architecture = context.architecture;
    const cap = Number.isFinite(remainingBudget) ? Math.max(0, remainingBudget - Number(reserve || 0)) : Infinity;
    const clinicallyAcceptable = equivalentClinicalPool(ranked, architecture.lowBudget ? 8 : 7)
      .filter(item => !Number.isFinite(cap) || !price(item.product) || price(item.product) <= cap + 0.01);
    if (!clinicallyAcceptable.length) return null;
    return choose(clinicallyAcceptable, profile, role, context);
  }

  function selectedProductsFromResult(result) {
    const order = ['oil_cleanser', 'cleanser', 'toner', 'essence', 'treatment', 'moisturizer', 'sunscreen'];
    return order.map(role => result[role] && result[role].product).filter(Boolean);
  }

  function routineTotalFromResult(result) {
    return selectedProductsFromResult(result).reduce((sum, product) => sum + price(product), 0);
  }

  function setRole(result, role, chosen, ranked) {
    result[role] = roleResultFromChoice(chosen, ranked);
    return result[role];
  }

  function reserveForRoles(catalog, profile, roles, selected, context) {
    let reserve = 0;
    const roleIndex = context && context.roleIndex ? context.roleIndex : buildRoleIndex(catalog);
    for (const role of roles) {
      const source = roleIndex[role] || [];
      let minimum = Infinity;
      for (const product of source) {
        const reasons = hardExclude(product, profile, role, selected || [], context || {});
        if (reasons.length) continue;
        const p = price(product);
        if (p > 0 && p < minimum) minimum = p;
      }
      if (!Number.isFinite(minimum)) return Infinity;
      reserve += minimum;
    }
    return reserve;
  }

  function selectMandatoryAnchor(catalog, profile, result, selected, context) {
    const budget = context.architecture.budget;
    let anchors = anchorRankings(catalog, profile, selected, context);
    let recoveryFallback = false;

    // Safety takes precedence over a corrective goal when the current barrier is
    // reactive/compromised and no treatment can safely address the requested
    // concern. In that case we still build a complete routine around a gentle
    // recovery anchor instead of returning an unusable "no treatment" result.
    if (!anchors.length && context.state && context.state.currentBarrierUnstable) {
      const recoveryContext = Object.assign({}, context, { anchorSelection: false });
      const recoveryRanked = rankRole(catalog, profile, 'treatment', selected || [], recoveryContext)
        .filter(item => {
          const p = professional(item.product), rs = p.roleSpecific || {}, e = p.strategyEvidence || {};
          const archetype = String(rs.archetype || '').toLowerCase();
          const recoveryEvidence = Math.max(Number(e.barrier_support || 0), Number(e.hydration || 0), Number(e.redness_support || 0));
          return p.anchorEligible && ['serum','ampoule'].includes(p.format) &&
            ['barrier_support','hydration','redness_support','supportive'].includes(archetype) &&
            recoveryEvidence >= 1.8 &&
            p.sensitiveSuitability !== 'low' && p.barrierCompatibility !== 'low' &&
            p.intensity === 'gentle';
        });
      if (recoveryRanked.length) {
        anchors = recoveryRanked.map(item => Object.assign({}, item, { anchorRole: 'recovery' }));
        recoveryFallback = true;
      }
    }
    if (!anchors.length) return false;
    const requiredLater = context.architecture.required.filter(role => role !== 'active_anchor');
    let feasible = anchors;
    if (budget) {
      const reserve = reserveForRoles(catalog, profile, requiredLater, selected, context);
      if (!Number.isFinite(reserve)) return false;
      const cap = Math.max(0, budget - reserve);
      feasible = anchors.filter(item => price(item.product) <= cap + 0.01);
    }
    if (!feasible.length) return false;
    const anchorContext = Object.assign({}, context, { anchorSelection: !recoveryFallback });
    const chosen = choose(feasible, profile, 'treatment', anchorContext);
    if (!chosen) return false;
    setRole(result, 'treatment', chosen, anchors);
    result.activeAnchor = { productId: chosen.product.id, role: 'treatment', primaryConcern: profile.primaryConcern, strategy: recoveryFallback ? 'barrier_support' : context.strategy.name, deferredPrimaryGoal: recoveryFallback };
    if (recoveryFallback) result.primaryGoalDeferred = true;
    selected.push(chosen.product);
    return true;
  }

  function addRequiredRole(catalog, profile, result, selected, role, remainingRequired, context) {
    const ranked = rankRole(catalog, profile, role, selected, context);
    if (!ranked.length) return false;
    const budget = context.architecture.budget;
    let chosen;

    if (budget) {
      const remaining = budget - routineTotalFromResult(result);
      const fitsBudgetAndReserve = item => {
        const itemPrice = price(item.product);
        if (itemPrice > remaining + 0.01) return false;
        if (!remainingRequired.length) return true;
        const futureSelected = selected.concat(item.product);
        const reserve = reserveForRoles(catalog, profile, remainingRequired, futureSelected, context);
        return Number.isFinite(reserve) && itemPrice + reserve <= remaining + 0.01;
      };
      // Prefer a clinically close support product, but under a tight budget the
      // support layer may compromise further before the therapeutic anchor is
      // sacrificed. All fallback candidates still passed hard eligibility.
      let feasible = equivalentClinicalPool(ranked, 10).filter(fitsBudgetAndReserve);
      if (!feasible.length) feasible = ranked.filter(fitsBudgetAndReserve);
      if (!feasible.length) return false;
      chosen = choose(feasible, profile, role, context);
    } else {
      chosen = choose(ranked, profile, role, context);
    }

    if (!chosen) return false;
    setRole(result, role, chosen, ranked);
    selected.push(chosen.product);
    return true;
  }

  function addOptionalRole(catalog, profile, result, selected, role, context) {
    if (selected.length >= context.architecture.maxProducts) return false;
    const budget = context.architecture.budget;
    const remaining = budget ? budget - routineTotalFromResult(result) : Infinity;
    if (budget && remaining <= 0.01) return false;

    if (role === 'support') {
      const combined = [];
      ['essence', 'toner'].forEach(actualRole => {
        rankRole(catalog, profile, actualRole, selected, context).slice(0, 10).forEach(item => combined.push(Object.assign({}, item, { actualRole })));
      });
      combined.sort((a, b) => b.evaluation.clinicalScore - a.evaluation.clinicalScore || b.evaluation.commercialScore - a.evaluation.commercialScore);
      const affordable = combined.filter(item => !budget || !price(item.product) || price(item.product) <= remaining + 0.01);
      if (!affordable.length) return false;
      const best = affordable[0];
      setRole(result, best.actualRole, best, combined.filter(x => x.actualRole === best.actualRole));
      selected.push(best.product);
      return true;
    }

    const ranked = rankRole(catalog, profile, role, selected, context);
    const affordable = ranked.filter(item => !budget || !price(item.product) || price(item.product) <= remaining + 0.01);
    if (!affordable.length) return false;
    const chosen = choose(affordable, profile, role, context);
    if (!chosen) return false;
    setRole(result, role, chosen, ranked);
    selected.push(chosen.product);
    return true;
  }

  function replaceWithGentler(catalog, profile, result, role, context) {
    const current = result[role];
    if (!current) return false;
    const currentLoad = productIrritation(current.product);
    if (currentLoad <= 0) return false;
    const others = selectedProductsFromResult(result).filter(p => p.id !== current.product.id);
    const replaceContext = Object.assign({}, context, { anchorSelection: result.activeAnchor && result.activeAnchor.role === role });
    const budget = context && context.architecture ? Number(context.architecture.budget || 0) : 0;
    const otherTotal = others.reduce((sum, item) => sum + price(item), 0);
    let ranked = rankRole(catalog, profile, role, others, replaceContext)
      .filter(item => item.product.id !== current.product.id)
      .filter(item => productIrritation(item.product) < currentLoad - 0.2)
      .filter(item => item.evaluation.clinicalScore >= current.clinicalScore - 8)
      .filter(item => role !== 'treatment' || primaryEvidence(item.product, profile, replaceContext) >= primaryEvidence(current.product, profile, replaceContext) - 0.15)
      .filter(item => !budget || otherTotal + price(item.product) <= budget + 0.01);
    if (result.activeAnchor && result.activeAnchor.role === role) ranked = ranked.filter(item => isActiveAnchorCandidate(item.product, profile, role, context));
    if (!ranked.length) return false;
    const candidate = ranked[0];
    result[role] = roleResultFromChoice(candidate, ranked);
    if (result.activeAnchor && result.activeAnchor.role === role) result.activeAnchor.productId = candidate.product.id;
    return true;
  }

  function ensureMakeupCleansingCapacity(catalog, profile, result, context) {
    if (profile.makeupFrequency !== 'most_days' || result.oil_cleanser || !result.cleanser) return false;
    const current = result.cleanser.product;
    const removal = String((professional(current).roleSpecific || {}).makeup_removal || 'low');
    if (removal === 'medium' || removal === 'high') return false;
    const others = selectedProductsFromResult(result).filter(p => p.id !== current.id);
    const budget = Number(context.architecture.budget || 0), otherTotal = others.reduce((sum,p)=>sum+price(p),0);
    const ranked = rankRole(catalog, profile, 'cleanser', others, context)
      .filter(item => ['medium','high'].includes(String((professional(item.product).roleSpecific||{}).makeup_removal||'')))
      .filter(item => !budget || otherTotal + price(item.product) <= budget + 0.01);
    if (!ranked.length) return false;
    result.cleanser = roleResultFromChoice(ranked[0], ranked);
    return true;
  }

  function validateAndRebalance(catalog, profile, result, context) {
    const changes = [];
    const budget = context.architecture.budget;
    if (ensureMakeupCleansingCapacity(catalog, profile, result, context)) changes.push('makeup_cleanser_upgraded');

    // Optional steps leave first if the routine exceeds irritation tolerance.
    for (let pass = 0; pass < 6; pass += 1) {
      const products = selectedProductsFromResult(result);
      const loads = routineIrritationLoads(products);
      const limit = routineIrritationLimit(profile);
      if (loads.max <= limit + 0.01) break;

      let removed = false;
      for (const role of ['toner', 'essence', 'oil_cleanser']) {
        if (result[role] && !(result.activeAnchor && result.activeAnchor.role === role)) {
          delete result[role];
          changes.push(`irritation_removed:${role}`);
          removed = true;
          break;
        }
      }
      if (removed) continue;

      const candidateRoles = selectedProductsFromResult(result)
        .map(p => p.role)
        .sort((a, b) => productIrritation(result[b] && result[b].product) - productIrritation(result[a] && result[a].product));
      let replaced = false;
      for (const role of candidateRoles) {
        if (replaceWithGentler(catalog, profile, result, role, context)) {
          changes.push(`irritation_replaced:${role}`);
          replaced = true;
          break;
        }
      }
      if (!replaced) break;
    }

    const total = routineTotalFromResult(result);
    const products = selectedProductsFromResult(result);
    const invalidBundle = products.some(p => professional(p).isBundle);
    const invalidSpf = !result.sunscreen || !professional(result.sunscreen.product).primarySpf;
    const lowEssenceAnchor = context.architecture.lowBudget && result.activeAnchor && result.activeAnchor.role === 'essence';

    result.professionalAudit = {
      strategy: context.strategy.name,
      skinState: context.state.state,
      irritation: routineIrritationLoads(products),
      irritationLimit: routineIrritationLimit(profile),
      invalidBundle,
      invalidSpf,
      lowBudgetEssenceAnchor: Boolean(lowEssenceAnchor),
      changes
    };
    result.budgetAudit = {
      requested: budget || null,
      total: Math.round(total * 100) / 100,
      withinBudget: !budget || total <= budget + 0.01,
      overBy: budget ? Math.max(0, Math.round((total - budget) * 100) / 100) : 0,
      changes: []
    };

    if (invalidBundle) result.error = 'Routine contains a bundle in a single-product slot';
    else if (invalidSpf) result.error = 'Routine has no valid primary sunscreen';
    else if (lowEssenceAnchor) result.error = 'Low-budget routine used an essence as therapeutic anchor';
    else if (budget && total > budget + 0.01) result.error = 'No professionally acceptable routine fits the selected budget';

    return result;
  }

  function ensureOutput(result, profile, context) {
    const products = selectedProductsFromResult(result);
    result.products = products;
    result.productIds = products.map(p => p.id);
    result.schedule = Object.fromEntries(products.map(p => [p.id, scheduleFor(p, profile)]));
    result.frequency = Object.fromEntries(products.map(p => [p.id, frequencyFor(p)]));
    result.morning = products.filter(p => {
      const s = scheduleFor(p, profile);
      return (s === 'AM' || s === 'AM/PM') && p.role !== 'oil_cleanser';
    });
    result.evening = products.filter(p => {
      const s = scheduleFor(p, profile);
      return (s === 'PM' || s === 'AM/PM') && p.role !== 'sunscreen';
    });
    result.estimatedTotal = Math.round(routineTotalFromResult(result) * 100) / 100;
    result.strategy = context.strategy;
    result.skinState = context.state;
    result.routineArchitecture = context.architecture;
    result.coreAudit = {
      activeAnchorPresent: Boolean(result.activeAnchor && products.some(p => p.id === result.activeAnchor.productId)),
      activeAnchor: result.activeAnchor || null,
      morningHasCleanser: result.morning.some(p => p.role === 'cleanser'),
      morningHasMoisturizer: result.morning.some(p => p.role === 'moisturizer'),
      morningHasSunscreen: result.morning.some(p => p.role === 'sunscreen'),
      eveningHasCleanser: result.evening.some(p => p.role === 'cleanser'),
      eveningHasMoisturizer: result.evening.some(p => p.role === 'moisturizer'),
      sharedCleanser: Boolean(result.cleanser && supportsWholeDayCore(result.cleanser.product, 'cleanser')),
      sharedMoisturizer: Boolean(result.moisturizer && supportsWholeDayCore(result.moisturizer.product, 'moisturizer'))
    };
    return result;
  }

  const ROUTINE_CACHE = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  function routineCacheKey(profile) {
    return JSON.stringify({
      skinType: profile.skinType, skinTypeModifiers: profile.skinTypeModifiers,
      primaryConcern: profile.primaryConcern, secondaryConcerns: profile.secondaryConcerns,
      sensitivity: profile.sensitivity, barrierState: profile.barrierState,
      activeExperience: profile.activeExperience, excludeRetinoids: profile.excludeRetinoids,
      excludeAcids: profile.excludeAcids, excludeSpicules: profile.excludeSpicules,
      routineStyle: profile.routineStyle, makeupFrequency: profile.makeupFrequency,
      budgetTotal: profile.budgetTotal, fragranceFreeOnly: profile.fragranceFreeOnly,
      veganOnly: profile.veganOnly, crueltyFreeOnly: profile.crueltyFreeOnly
    });
  }

  function recommendRoutine(catalog, inputProfile) {
    const profile = normalizeProfile(inputProfile);
    if (ROUTINE_CACHE && catalog && typeof catalog === 'object') {
      let cache = ROUTINE_CACHE.get(catalog);
      if (!cache) { cache = new Map(); ROUTINE_CACHE.set(catalog, cache); }
      const key = routineCacheKey(profile);
      const hit = cache.get(key);
      if (hit) return hit;
      const result = recommendRoutineUncached(catalog, profile);
      if (cache.size > 1024) cache.delete(cache.keys().next().value);
      cache.set(key, result);
      return result;
    }
    return recommendRoutineUncached(catalog, profile);
  }

  function recommendRoutineUncached(catalog, inputProfile) {
    const profile = normalizeProfile(inputProfile);
    const state = strategyModule().analyzeSkinState(profile);
    const strategy = strategyModule().generateStrategy(profile, state);
    const architecture = strategyModule().buildRoutineArchitecture(profile, strategy, state);
    const context = { profile, state, strategy, architecture, roleIndex: buildRoleIndex(catalog) };
    const result = { profile, strategy, skinState: state, routineArchitecture: architecture };
    const selected = [];

    if (!selectMandatoryAnchor(catalog, profile, result, selected, context)) {
      result.error = architecture.budget ? 'No professionally acceptable treatment fits the selected budget while preserving the four core steps' : 'No professionally acceptable therapeutic anchor was found';
      result.missingRole = 'active_anchor';
      return ensureOutput(result, profile, context);
    }

    const remainingRequired = architecture.required.filter(role => role !== 'active_anchor');
    for (let i=0;i<remainingRequired.length;i+=1) {
      const role=remainingRequired[i], future=remainingRequired.slice(i+1);
      if (!addRequiredRole(catalog, profile, result, selected, role, future, context)) {
        result.error = `No professionally acceptable ${role} fits the routine architecture`;
        result.missingRole = role; break;
      }
    }

    if (!result.error) {
      for (const role of architecture.optional || []) addOptionalRole(catalog, profile, result, selected, role, context);
    }
    validateAndRebalance(catalog, profile, result, context);
    return ensureOutput(result, profile, context);
  }

  function routineHasActiveAnchor(result, inputProfile) {
    if (!result || !result.activeAnchor) return false;
    const product = selectedProductsFromResult(result).find(p => p.id === result.activeAnchor.productId);
    return Boolean(product && isActiveAnchorCandidate(product, inputProfile, result.activeAnchor.role, { architecture: result.routineArchitecture }));
  }

  function compatibleRoutine(products, inputProfile) {
    const profile = normalizeProfile(inputProfile);
    const state = strategyModule().analyzeSkinState(profile);
    const strategy = strategyModule().generateStrategy(profile, state);
    const architecture = strategyModule().buildRoutineArchitecture(profile, strategy, state);
    const context = { state, strategy, architecture };
    const selected = [];
    for (const product of products || []) {
      const role = product.role;
      const reasons = hardExclude(product, profile, role, selected, context);
      if (reasons.length) return { compatible: false, reasons, product };
      selected.push(product);
    }
    return { compatible: true, reasons: [] };
  }

  function auditRegression(catalog, profiles) {
    return (profiles || []).map((profile, index) => {
      const routine = recommendRoutine(catalog, profile);
      return {
        index,
        profile: normalizeProfile(profile),
        strategy: routine.strategy && routine.strategy.name,
        skinState: routine.skinState && routine.skinState.state,
        error: routine.error || null,
        products: (routine.products || []).map(p => ({ id: p.id, role: p.role, title: p.title, vendor: p.vendor })),
        total: routine.estimatedTotal || 0,
        budgetAudit: routine.budgetAudit || null
      };
    });
  }

  // Compatibility alias. v14 no longer repairs an over-budget routine after selection;
  // budget feasibility is enforced while selecting every required/optional slot.
  function optimizeWholeDayBudget(catalog, inputProfile, result) {
    const profile = normalizeProfile(inputProfile);
    const total = routineTotalFromResult(result || {});
    const budget = Number(profile.budgetTotal || 0);
    result.budgetAudit = {
      requested: budget || null,
      total: Math.round(total * 100) / 100,
      withinBudget: !budget || total <= budget + 0.01,
      overBy: budget ? Math.max(0, Math.round((total - budget) * 100) / 100) : 0,
      changes: ['v14_budget_enforced_during_selection']
    };
    return result;
  }

  window.SkinQuizEngine = {
    recommendRoutine,
    rankRole,
    scoreProduct,
    normalizeProfile,
    compatibleRoutine,
    auditRegression,
    attrs,
    professional,
    productIrritation,
    routineIrritationLoads,
    routineIrritationLimit,
    moisturizerWeight,
    spfUsage,
    cleanserIntensity,
    isActiveAnchorCandidate,
    isPrimaryTreatmentFormat,
    routineHasActiveAnchor,
    supportsWholeDayCore,
    optimizeWholeDayBudget,
    isStandardFaceScope,
    stableHash,
    profileSignature,
    constants: { ENGINE_VERSION, ROLES, PREFERRED_BRANDS }
  };
})();
